import { getClientEnv } from '@/env/client'

import { renderTemplate, type MessageTemplate, type TemplateValues } from './template'

interface FailurePolicy {
    readonly production: boolean
    readonly log: (message: string, context: Record<string, unknown>) => void
}

/** Logged from the client only: during server render the template has the values the
 *  server rendered its copy with, and a failure there is the copy set's to report. */
const logOnClient: FailurePolicy['log'] = (message, context) => {
    if (typeof window !== 'undefined') {
        getClientEnv().logger.withMetadata(context).error(message)
    }
}

/**
 * A client template's text for the client's values, each read as the kind its copy set
 * types it. A template that cannot render fails the
 * render outside production, as a copy set's message does; in production it is empty text
 * and an error naming the file and key.
 */
export function renderClientTemplate(
    template: MessageTemplate,
    values: TemplateValues,
    {
        production = process.env.NODE_ENV === 'production',
        log = logOnClient,
    }: Partial<FailurePolicy> = {},
): string {
    return renderTemplate(template, values).match({
        Ok: (text) => text,
        Err: (failure) => {
            const missing = failure.cause === 'missing-variable' ? [...failure.missing] : undefined
            if (!production) {
                throw new Error(
                    `${template.file} "${template.key}" is unrenderable (${failure.cause}${
                        missing ? `: ${missing.join(', ')}` : ''
                    })`,
                )
            }
            log('Client template is unrenderable', {
                file: template.file,
                key: template.key,
                cause: failure.cause,
                ...(missing ? { missing } : {}),
            })
            return ''
        },
    })
}
